/**
 * Pure-function decision for "should we show the share-profile prompt
 * when the user sends a first message to this peer?"
 *
 * Signal's policy is fail-closed: do not auto-share, and only ever
 * prompt once per peer. The prompt is suppressed entirely when the
 * share is already established by another path (the peer is in the
 * roster; the peer is a co-member of a group we've already exposed our
 * profile to) or when the user has already given an answer for this peer.
 *
 * All inputs come from the caller; nothing about the peer is sourced
 * from the server. In particular, the context does NOT carry any
 * server-supplied display-name claim — a malicious server cannot
 * influence whether we prompt by lying about who the peer claims to be.
 *
 * Rules apply in order — the first match wins, so suppression categories
 * are mutually exclusive at decision time even when more than one applies:
 *
 *   1. existingDecision === 'sharing'   → 'skipAlreadyShared'
 *   2. isPeerInRoster                   → 'skipInRoster'
 *   3. isPeerInSharedGroup              → 'skipCoMember'
 *   4. existingDecision === 'declined'  → 'skipDeclined'
 *   5. existingDecision === 'revoked'   → 'skipRevoked'
 *   6. (undecided + alone)              → 'showPrompt'
 *
 * Rule priority is deliberate: a peer added to the roster after a prior
 * decline does not re-trigger a prompt; the reason is `'skipInRoster'`
 * (the stronger consent path).
 *
 * Mirrors kaether_for_ios/Stoat/E2EE/ProfileSharePromptPolicy.swift.
 */

import type { ProfileShareDecision } from "./ProfileShareConsent";

/**
 * All inputs used by the policy. Every field is sourced locally or from
 * the caller; NO server-supplied peer-identity field is included.
 *
 * INVARIANT: this interface must NEVER gain a `peerClaimedName`,
 * `peerClaimedAvatar`, or equivalent server-supplied display field.
 * The self-test suite asserts the exact key set to catch future
 * refactors that would silently re-introduce one.
 *
 * Mirrors `ProfileSharePromptContext` in the iOS implementation.
 */
export interface ProfileSharePromptContext {
  /** The peer this outbound send is addressed to. */
  readonly peerUserId: string;
  /** True if the peer is already in the local contact roster. */
  readonly isPeerInRoster: boolean;
  /** True if the peer is a co-member of a group the user has joined. */
  readonly isPeerInSharedGroup: boolean;
  /** Existing persisted decision for this peer (defaults 'undecided'). */
  readonly existingDecision: ProfileShareDecision;
  /** Whether an outbound send is currently in progress. */
  readonly isOutboundSendInProgress: boolean;
}

/**
 * Decision returned by `ProfileSharePromptPolicy.decide`.
 *
 * - `'showPrompt'`         — present the share-profile modal to the user.
 * - `'skipAlreadyShared'`  — profile is already shared; no prompt needed.
 * - `'skipInRoster'`       — peer is a contact; profile visible by other path.
 * - `'skipCoMember'`       — peer is a group co-member; no prompt needed.
 * - `'skipDeclined'`       — user previously said no; do not re-surface.
 * - `'skipRevoked'`        — user revoked; do not re-surface.
 */
export type ProfileSharePromptDecision =
  | "showPrompt"
  | "skipAlreadyShared"
  | "skipInRoster"
  | "skipCoMember"
  | "skipDeclined"
  | "skipRevoked";

/**
 * Pure-function prompt policy. Deterministic given the same inputs;
 * no I/O, no async, no side effects.
 *
 * Import `ProfileSharePromptPolicy.decide` at the outbound-send call
 * site; inject the inputs from local state only.
 */
export const ProfileSharePromptPolicy = {
  /**
   * Decide whether to show the share-profile prompt.
   *
   * Rules are applied in priority order — the first matching rule wins.
   */
  decide(ctx: ProfileSharePromptContext): ProfileSharePromptDecision {
    // Rule 1: already sharing — no prompt needed.
    if (ctx.existingDecision === "sharing") return "skipAlreadyShared";
    // Rule 2: peer is in the local contact roster — profile exposed via
    //         the contact path; no separate share-profile consent needed.
    if (ctx.isPeerInRoster) return "skipInRoster";
    // Rule 3: peer is a co-member of a shared group — profile already
    //         exposed through group context.
    if (ctx.isPeerInSharedGroup) return "skipCoMember";
    // Rule 4: user previously declined — do not re-surface the prompt.
    if (ctx.existingDecision === "declined") return "skipDeclined";
    // Rule 5: user previously revoked — monotonically sticky.
    if (ctx.existingDecision === "revoked") return "skipRevoked";
    // Rule 6: default — undecided non-contact → show the prompt.
    return "showPrompt";
  },
} as const;
